import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SiteHeader } from '../../components/SiteHeader';
import { SiteFooter } from '../../components/SiteFooter';

export interface LegalSection {
  id: string;
  title: string;
  body: ReactNode;
}

interface Props {
  /** Translation-key prefix used to derive the eyebrow / title / lastUpdated copy. */
  i18nKey: string;
  /** Hand-set ISO date the document was last revised. */
  lastUpdated: string;
  /** Lead paragraph rendered above the section list. */
  lead?: ReactNode;
  sections: LegalSection[];
  /**
   * Optional one-line counsel-review note rendered in a calm pearl card at
   * the foot of the document. When omitted, a tasteful default is shown.
   * Pass `counselNote={null}` to suppress it entirely.
   */
  counselNote?: ReactNode | null;
}

const DEFAULT_COUNSEL_NOTE =
  'This document reflects our current practices and is provided for transparency. ' +
  'It is not legal advice — we recommend reviewing it with your own counsel before relying on it.';

/**
 * Shared chrome for /legal/* + /about + /contact + /changelog. Renders a
 * left-side sticky TOC on wide screens and an inline anchor list on
 * mobile, scroll-spying to highlight the active section.
 */
export function LegalPageLayout({ i18nKey, lastUpdated, lead, sections, counselNote }: Props) {
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState(sections[0]?.id);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible?.target.id) setActive(visible.target.id);
      },
      { rootMargin: '-30% 0px -60% 0px' },
    );
    for (const s of sections) {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    }
    return () => obs.disconnect();
  }, [sections]);

  const formattedDate = new Intl.DateTimeFormat(i18n.language, {
    year: 'numeric', month: 'long', day: 'numeric',
  }).format(new Date(lastUpdated));

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />
      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-12 lg:py-16">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4">
            <span className="sparkle-dot" aria-hidden="true" />
            {t(`${i18nKey}.eyebrow`)}
          </div>
          <h1 className="display text-4xl lg:text-5xl font-semibold tracking-tight">
            {t(`${i18nKey}.title`)}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground num">
            {t('legalShared.lastUpdated')}: {formattedDate}
          </p>
          {lead && <div className="mt-6 text-base text-muted-foreground leading-relaxed">{lead}</div>}
        </div>

        <div className="mt-12 grid lg:grid-cols-[200px_1fr] gap-12">
          <nav aria-label="Table of contents" className="hidden lg:block">
            <div className="sticky top-24 space-y-1">
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
                {t('legalShared.contents')}
              </div>
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className={
                    'block text-sm py-1 leading-snug transition-colors ' +
                    (active === s.id
                      ? 'text-accent-700 font-medium'
                      : 'text-muted-foreground hover:text-foreground')
                  }
                >
                  {s.title}
                </a>
              ))}
            </div>
          </nav>

          <article className="max-w-3xl">
            {sections.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-24 mb-12">
                <h2 className="display text-2xl font-semibold tracking-tight mb-4">
                  {s.title}
                </h2>
                <div className="prose-plyez">{s.body}</div>
              </section>
            ))}

            {counselNote !== null && (
              <div className="rule mb-8" aria-hidden="true" />
            )}
            {counselNote !== null && (
              <aside className="rounded-2xl border border-border bg-card px-5 py-4 text-sm text-muted-foreground leading-relaxed">
                {counselNote ?? DEFAULT_COUNSEL_NOTE}
              </aside>
            )}
          </article>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

/**
 * Convenience paragraph component — keeps the markup terse inside
 * the per-page section arrays.
 */
export function P({ children }: { children: ReactNode }) {
  return <p className="text-foreground/85 leading-relaxed mb-4">{children}</p>;
}

export function H3({ children }: { children: ReactNode }) {
  return (
    <h3 className="display text-lg font-semibold tracking-tight text-foreground mt-8 mb-3">
      {children}
    </h3>
  );
}

export function UL({ children }: { children: ReactNode }) {
  return <ul className="list-disc pl-6 mb-4 space-y-1 text-foreground/85 marker:text-accent-500">{children}</ul>;
}

export function LI({ children }: { children: ReactNode }) {
  return <li className="leading-relaxed">{children}</li>;
}

export function Strong({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-foreground">{children}</strong>;
}

export function ContactLink({ to, children }: { to: string; children: ReactNode }) {
  return <Link to={to} className="text-accent-700 underline underline-offset-2 hover:text-accent-800">{children}</Link>;
}
